import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import styles from "../views/FillInTheBlanks.module.css";
import useJourneyLogic from "../hooks/useJourneyLogic";
import LoadingSpinner from "../components/LoadingSpinner";
import { usePreloadedData } from "../context/PreloadContext";

function FillInTheBlanksJourneyView({ onComplete, gameLength = 2 }) {
    const { user } = useAuth();
    const autoAdvanceTimeoutRef = useRef(null);

    // --- Local State (Simplified for Journey) ---
    const [selected, setSelected] = useState([]);
    const [showExplanation, setShowExplanation] = useState(false);
    const [localScore, setLocalScore] = useState(0); // Renamed 'score' to 'localScore' for consistency
    const [done, setDone] = useState(false); // Local state to trigger end
    const [isCorrect, setIsCorrect] = useState(false);
    const [timer, setTimer] = useState(30);
    const [showInstructions, setShowInstructions] = useState(true);

    const ACTIVITY_TYPE = "ayah-blanks";
    const { preloadedData } = usePreloadedData();

    // --- Journey Logic Hook ---
    const {
        loadingQuestions,
        error,
        loadQuizQuestions,
        questions,
        current,
        setCurrent,
        isGameEnded,
        handleCorrectAttempt,
        handleIncorrectAttempt,
    } = useJourneyLogic({
        activityType: ACTIVITY_TYPE,
        // The game length here ensures the user only sees 'gameLength' questions total
        gameSpecificEndCondition: done,
        gameLength,
        onComplete,
    });

    // --- Load questions on mount ---
    useEffect(() => {
        if (!user) return;
        // Removed resetGameLogicStates as it's internal to the hook now
        // Use optional chaining to avoid accessing properties of null when preload hasn't finished
        if (preloadedData?.fillintheblanks) {
            loadQuizQuestions(preloadedData.fillintheblanks);
        } else {
            loadQuizQuestions();
        }
    }, [user, loadQuizQuestions, preloadedData]);

    // --- Start quiz ---
    const startQuiz = useCallback(() => {
        setShowInstructions(false);
        setTimer(30);
    }, []);

    // --- Next question/End Game ---
    const handleAdvance = useCallback(() => {
        setSelected([]);
        setShowExplanation(false);
        setTimer(30);
        setIsCorrect(false);

        if (current + 1 < questions.length) {
            setCurrent((prev) => prev + 1);
        } else {
            setDone(true); // Signal completion to the useJourneyLogic hook
        }
    }, [current, questions.length, setCurrent]);

    // --- Timeout logic ---
    const handleTimeout = useCallback(() => {
        if (!questions.length || current >= questions.length) return;
        setShowExplanation(true);
        setIsCorrect(false);
        // Track as incorrect attempt
        handleIncorrectAttempt(questions[current]._id);
    }, [handleIncorrectAttempt, questions, current]);

    // --- Timer effect ---
    useEffect(() => {
        if (
            showExplanation ||
            done ||
            loadingQuestions ||
            questions.length === 0 ||
            showInstructions
        )
            return;

        const interval = setInterval(() => {
            setTimer((prev) => {
                if (prev === 1) {
                    clearInterval(interval);
                    handleTimeout();
                    return 30;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [
        current,
        showExplanation,
        done,
        loadingQuestions,
        questions.length,
        showInstructions,
        handleTimeout,
    ]);

    // --- Auto advance after explanation ---
    useEffect(() => {
        if (!showExplanation) return;
        autoAdvanceTimeoutRef.current = setTimeout(() => {
            handleAdvance();
        }, 8000);
        return () => clearTimeout(autoAdvanceTimeoutRef.current);
    }, [showExplanation, handleAdvance]);

    // --- Select word ---
    const handleSelect = useCallback(
        (word) => {
            if (showExplanation) return;
            if (!questions.length || current >= questions.length) return;

            const maxBlanks = questions[current].blanks.length;
            if (selected.includes(word)) {
                setSelected(selected.filter((w) => w !== word));
            } else {
                if (selected.length < maxBlanks) {
                    setSelected([...selected, word]);
                } else {
                    // Replace the last selected word with the new one
                    setSelected([...selected.slice(0, -1), word]);
                }
            }
        },
        [showExplanation, questions, current, selected]
    );

    // --- Submit answer ---
    const handleSubmit = useCallback(() => {
        if (showExplanation) return;
        if (!questions.length || current >= questions.length) return;

        const currentQ = questions[current];

        // Check if selected words exactly match the correct blanks array (in order)
        const correct =
            selected.length === currentQ.blanks.length &&
            selected.every((word, idx) => word === currentQ.blanks[idx]);

        setLocalScore((prev) => (correct ? prev + 1 : prev));
        setIsCorrect(correct);
        setShowExplanation(true);

        if (correct) {
            handleCorrectAttempt(currentQ._id);
        } else {
            handleIncorrectAttempt(currentQ._id);
        }
    }, [
        selected,
        current,
        questions,
        showExplanation,
        handleCorrectAttempt,
        handleIncorrectAttempt,
    ]);

    // --- Early returns ---
    const backgroundStyle = { backgroundImage: `url("/images/blankkk.jpg")` };

    if (isGameEnded) {
        return null;
    }

    if (!user)
        return (
            <p className={styles.message}>Please log in to use this feature.</p>
        );

    if (loadingQuestions || error || questions.length === 0) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.container}>
                    {loadingQuestions && <LoadingSpinner />}
                    {error && (
                        <>
                            <p className={styles.errorMessage}>Error: {error}</p>
                            <button
                                onClick={loadQuizQuestions}
                                className={styles.proceedButton}
                            >
                                Retry Loading Questions
                            </button>
                        </>
                    )}
                    {questions.length === 0 && !loadingQuestions && (
                        <p className={styles.message}>
                            No questions found. Please check question data.
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const currentQ = questions[current];
    const progressPercent = ((current + 1) / questions.length) * 100;

    return (
        <div className={styles.background} style={backgroundStyle}>
            <div className={styles.overlay}></div>
            <div className={styles.container}>
                <h2 className={styles.title}>📖 Journey Activity: Fill in the Blanks</h2>

                {/* --- Instructions --- */}
                {showInstructions ? (
                    <div className={styles.instructionsPopup}>
                        <h3>Journey Mode Instructions:</h3>
                        <ul>
                            <li>An Ayah from the Quran will be displayed with missing words.</li>
                            <li>Select the correct words from the options provided in the correct order.</li>
                            <li>Your goal is completion to advance in your daily journey.</li>
                        </ul>
                        <button className={styles.proceedButton} onClick={startQuiz}>
                            Start Activity
                        </button>
                    </div>
                ) : (
                    <>
                        {/* --- Progress --- */}
                        <p className={styles.progressText}>
                            Question {current + 1} of {questions.length} (Score: {localScore})
                        </p>
                        <div className={styles.progressWrapper}>
                            <div
                                className={styles.progressBar}
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        {/* Removed Lives/Hearts UI */}

                        {!showExplanation && (
                            <div className={styles.timer}>⏱️ {timer} sec</div>
                        )}

                        <p className={styles.ayah}>{currentQ.ayahText}</p>

                        <div className={styles.options}>
                            {currentQ.options.map((word, i) => (
                                <button
                                    key={i}
                                    onClick={() => handleSelect(word)}
                                    className={`${styles.option} ${
                                        selected.includes(word) ? styles.selected : ""
                                    }`}
                                    disabled={
                                        showExplanation ||
                                        (selected.length === currentQ.blanks.length &&
                                            !selected.includes(word))
                                    }
                                >
                                    {word}
                                </button>
                            ))}
                        </div>

                        <p className={styles.selectedDisplay}>
                            Selected: {selected.join(", ") || "None"}
                        </p>

                        {!showExplanation && (
                            <button
                                className={styles.submit}
                                onClick={handleSubmit}
                                disabled={selected.length !== currentQ.blanks.length}
                            >
                                Submit
                            </button>
                        )}

                        {/* --- Explanation --- */}
                        {showExplanation && (
                            <div className={styles.explanationPopup}>
                                <p className={isCorrect ? styles.correctText : styles.incorrectText}>
                                    {isCorrect ? "✅ Correct, MashaAllah!" : "❌ Incorrect."}
                                </p>
                                <p>
                                    <strong>Correct Answer:</strong>{" "}
                                    {currentQ.blanks.join(", ")}
                                </p>
                                <p className={styles.reference}>
                                    <em>{currentQ.reference}</em>
                                </p>
                                <button
                                    className={styles.nextButton}
                                    onClick={handleAdvance}
                                >
                                    {current + 1 < questions.length
                                        ? "Next Question"
                                        : "Complete Activity"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
            {/* Removed GamePopups and FeedbackModal */}
        </div>
    );
}

export default FillInTheBlanksJourneyView;
